import { NativePlatform } from '@citrite/workspace-ui-platform';
import { logError } from 'remoteLogging';
import { sendClipboardMessageToChromeApp } from 'Environment/ChromeApp/ChromeAppBridge';
import { ChromeAppResponse } from 'Environment/ChromeApp/types';
import { environment } from 'Environment/Environment';
import { isCitrixChromeApp } from 'Environment/launchResource/device';

export function shouldEnableSiri() {
	return (
		environment.nativePlatform === NativePlatform.iOS &&
		!environment.nativeCapabilities?.clientSettings?.disableSiri
	);
}

export const reconnectAppsEvent = 'reconnectApps';

// Editable section types in an ica file
export enum SectionType {
	SessionLaunchTelemetry = 'Session Launch Telemetry',
}

/**
 * Modifies or adds a section in the given ICA file content with the provided data.
 * @param icaFile The content of the ica file as a string
 * @param section The section in the ica file to be modified or added
 * @param data The data to be added or modified in the specified section
 * @returns The modified ica file content
 */
export const modifyIcaFileContent = (
	icaFile: string,
	section: SectionType,
	data: object
): string => {
	try {
		if (!icaFile || icaFile.trim() === '') {
			return icaFile;
		}
		const sectionToFind = `[${section}]`;

		if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
			return icaFile;
		}

		// If the section is not added already by the storefront then we will not modify the icaFile and it will remain unchanged
		if (icaFile.includes(sectionToFind)) {
			const newEntries = Object.entries(data).map(([key, value]) => `${key}=${value}`);
			const stringToInsert = newEntries.join('\n');
			icaFile = icaFile.trim() + '\n';
			icaFile = icaFile.replace(sectionToFind, `${sectionToFind}\n${stringToInsert}`);
		}

		return icaFile;
	} catch (error) {
		logError(error, {
			additionalContext: {
				error,
			},
			tags: {
				feature: 'modifyIcaFileContent',
			},
		});
		return icaFile;
	}
};

/**
 * @param text
 * @returns Promise that resolves when the text is copied to clipboard or rejects if clipboard operation is not supported
 */
export async function copyToClipboard(text: string): Promise<ChromeAppResponse | void> {
	if (isCitrixChromeApp()) {
		return sendClipboardMessageToChromeApp({ data: text });
	}

	if (navigator?.clipboard?.writeText) {
		return navigator.clipboard.writeText(text);
	}

	return Promise.reject(
		new Error('Clipboard operations are not supported on this platform')
	);
}
